// Delivery areas (DECISIONS, 22 October): a shop mixes rules and the buyer's
// drop-off pin picks the fee. The Worker resolves the fee at checkout and for
// quotes; the app only describes the rules and shows the Worker's quote.
//
// - place: a neighbourhood, town, district, region or country as Google
//   names it ("Anywhere in Accra GHC 30"). The Worker asks Google which
//   places the pin is in.
// - circle: a circle the shop drags anywhere and resizes.
// - per_km: a base fee plus a fee per started km from the shop, up to a limit.
// - anywhere: the fee when no other rule matches ("Rest of Ghana").
// The cheapest matching rule wins, unless a rule is marked "always this fee
// here" (priority): then the cheapest matching priority rule wins.

import { distanceMeters, isLatLng, type LatLng } from './location.ts';
import { formatMoney } from './money.ts';

export const PLACE_LEVELS = ['neighbourhood', 'town', 'district', 'region', 'country'] as const;
export type PlaceLevel = (typeof PLACE_LEVELS)[number];
export const PLACE_LEVEL_LABELS: Record<PlaceLevel, string> = { neighbourhood: 'Area', town: 'Town or city', district: 'District', region: 'Region', country: 'Country' };

export type DeliveryRule =
  // viewport only helps draw the place again; matching uses placeId or name.
  | { kind: 'place'; name: string; level: PlaceLevel; placeId: string | null; viewport: Viewport | null; feeMinor: number; priority: boolean }
  | { kind: 'circle'; name: string; center: LatLng; radiusMeters: number; feeMinor: number; priority: boolean }
  | { kind: 'per_km'; baseMinor: number; perKmMinor: number; maxMeters: number; priority: boolean }
  | { kind: 'anywhere'; feeMinor: number };

export type Viewport = { north: number; south: number; east: number; west: number };

// A place the buyer's pin is in, from Google's reverse geocoding.
export type PinPlace = { placeId: string; name: string; level: PlaceLevel };

export const MAX_RULES = 20;
export const MIN_CIRCLE_METERS = 200;
export const MAX_CIRCLE_METERS = 200_000;
export const MAX_PER_KM_METERS = 300_000;
const MAX_FEE_MINOR = 1_000_000;
const PLACE_ID = /^[A-Za-z0-9_-]{4,300}$/;

function whole(value: unknown, min: number, max: number): number | null {
  const number = Number(value);
  return Number.isInteger(number) && number >= min && number <= max ? number : null;
}

function text(value: unknown, max: number): string {
  return String(value ?? '').replace(/\s+/g, ' ').trim().slice(0, max);
}

function readViewport(value: unknown): Viewport | null {
  const box = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
  const sides = [box.north, box.south, box.east, box.west].map(Number);
  if (!sides.every(Number.isFinite)) return null;
  const [north, south, east, west] = sides as [number, number, number, number];
  return north >= south && Math.abs(north) <= 90 && Math.abs(south) <= 90 && Math.abs(east) <= 180 && Math.abs(west) <= 180
    ? { north: Number(north.toFixed(6)), south: Number(south.toFixed(6)), east: Number(east.toFixed(6)), west: Number(west.toFixed(6)) } : null;
}

export type RulesParse = { ok: true; value: DeliveryRule[] } | { ok: false; message: string };

export function parseDeliveryRules(raw: unknown): RulesParse {
  const input = Array.isArray(raw) ? raw : [];
  if (!input.length) return { ok: false, message: 'Add at least one delivery area.' };
  if (input.length > MAX_RULES) return { ok: false, message: `Use up to ${MAX_RULES} delivery areas.` };
  const rules: DeliveryRule[] = [];
  for (const item of input) {
    const rule = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>;
    const priority = rule.priority === true;
    const feeMinor = whole(rule.feeMinor, 0, MAX_FEE_MINOR);
    if (rule.kind === 'place') {
      const name = text(rule.name, 80);
      const level = PLACE_LEVELS.includes(rule.level as PlaceLevel) ? rule.level as PlaceLevel : null;
      if (name.length < 2 || !level) return { ok: false, message: 'Choose each place from the search, or type its name and what kind of place it is.' };
      if (feeMinor === null) return { ok: false, message: `Give ${name} a fee, or 0 for free.` };
      const placeId = typeof rule.placeId === 'string' && PLACE_ID.test(rule.placeId) ? rule.placeId : null;
      rules.push({ kind: 'place', name, level, placeId, viewport: readViewport(rule.viewport), feeMinor, priority });
    } else if (rule.kind === 'circle') {
      const name = text(rule.name, 60) || 'Circle';
      const radiusMeters = whole(rule.radiusMeters, MIN_CIRCLE_METERS, MAX_CIRCLE_METERS);
      if (!isLatLng(rule.center)) return { ok: false, message: `Place “${name}” on the map.` };
      if (radiusMeters === null) return { ok: false, message: `Make “${name}” between ${MIN_CIRCLE_METERS / 1000} and ${MAX_CIRCLE_METERS / 1000} km across the middle.` };
      if (feeMinor === null) return { ok: false, message: `Give “${name}” a fee, or 0 for free.` };
      const center = { lat: Number(rule.center.lat.toFixed(6)), lng: Number(rule.center.lng.toFixed(6)) };
      rules.push({ kind: 'circle', name, center, radiusMeters, feeMinor, priority });
    } else if (rule.kind === 'per_km') {
      const baseMinor = whole(rule.baseMinor, 0, MAX_FEE_MINOR);
      const perKmMinor = whole(rule.perKmMinor, 1, MAX_FEE_MINOR);
      const maxMeters = whole(rule.maxMeters, 1_000, MAX_PER_KM_METERS);
      if (baseMinor === null || perKmMinor === null) return { ok: false, message: 'Give the per km rule a base fee and a fee per km.' };
      if (maxMeters === null) return { ok: false, message: `Set how far per km delivery goes, 1 to ${MAX_PER_KM_METERS / 1000} km.` };
      rules.push({ kind: 'per_km', baseMinor, perKmMinor, maxMeters, priority });
    } else if (rule.kind === 'anywhere') {
      if (feeMinor === null) return { ok: false, message: 'Give “Everywhere else” a fee.' };
      if (rules.some(item => item.kind === 'anywhere')) return { ok: false, message: 'Add “Everywhere else” only once.' };
      rules.push({ kind: 'anywhere', feeMinor });
    } else {
      return { ok: false, message: 'One delivery area is not a kind Guildite knows.' };
    }
  }
  return { ok: true, value: rules };
}

// Reads stored rules leniently (bad entries are dropped, not fatal).
export function readDeliveryRules(raw: unknown): DeliveryRule[] {
  return (Array.isArray(raw) ? raw : []).flatMap(item => {
    const parsed = parseDeliveryRules([item]);
    return parsed.ok ? parsed.value : [];
  });
}

export function rulesNeedPlaces(rules: DeliveryRule[]): boolean {
  return rules.some(rule => rule.kind === 'place');
}

export function rulesNeedShopPoint(rules: DeliveryRule[]): boolean {
  return rules.some(rule => rule.kind === 'per_km');
}

const normalize = (value: string) => value.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, ' ').trim();

// A typed place matches by name at the same level; a searched place by its id.
function placeMatches(rule: Extract<DeliveryRule, { kind: 'place' }>, places: PinPlace[]): boolean {
  return places.some(place => (rule.placeId && place.placeId === rule.placeId) || (place.level === rule.level && normalize(place.name) === normalize(rule.name)));
}

export type PinContext = { point: LatLng | null; shopPoint: LatLng | null; places: PinPlace[] | null };
export type RuleFee = { feeMinor: number; rule: DeliveryRule } | null;

function ruleFee(rule: DeliveryRule, pin: PinContext): number | null {
  if (rule.kind === 'circle') return pin.point && distanceMeters(rule.center, pin.point) <= rule.radiusMeters ? rule.feeMinor : null;
  if (rule.kind === 'place') return pin.places && placeMatches(rule, pin.places) ? rule.feeMinor : null;
  if (rule.kind === 'per_km') {
    if (!pin.point || !pin.shopPoint) return null;
    const meters = distanceMeters(pin.shopPoint, pin.point);
    return meters <= rule.maxMeters ? rule.baseMinor + Math.max(1, Math.ceil(meters / 1000)) * rule.perKmMinor : null;
  }
  return null;
}

// The fee for a drop-off, or null when no rule covers it.
export function resolveDeliveryFee(rules: DeliveryRule[], pin: PinContext): RuleFee {
  const matched = rules.flatMap(rule => {
    const fee = ruleFee(rule, pin);
    return fee === null ? [] : [{ feeMinor: fee, rule }];
  });
  const priority = matched.filter(item => 'priority' in item.rule && item.rule.priority);
  const pool = priority.length ? priority : matched;
  if (pool.length) return pool.reduce((best, item) => (item.feeMinor < best.feeMinor ? item : best));
  const anywhere = rules.find(rule => rule.kind === 'anywhere');
  return anywhere ? { feeMinor: anywhere.feeMinor, rule: anywhere } : null;
}

const fee = (minor: number) => (minor ? formatMoney(minor) : 'free');
const km = (meters: number) => `${Number((meters / 1000).toFixed(1))} km`;

// "Anywhere in Accra · GHC 30", "Osu circle (2 km) · GHC 10".
export function describeRule(rule: DeliveryRule): string {
  if (rule.kind === 'place') return `Anywhere in ${rule.name} · ${fee(rule.feeMinor)}`;
  if (rule.kind === 'circle') return `${rule.name} (${km(rule.radiusMeters)} around) · ${fee(rule.feeMinor)}`;
  if (rule.kind === 'per_km') return `${formatMoney(rule.baseMinor)} + ${formatMoney(rule.perKmMinor)} per km, up to ${km(rule.maxMeters)} from the shop`;
  return `Everywhere else · ${fee(rule.feeMinor)}`;
}

// The kind of place a Google result is, or null for streets and businesses.
export function placeLevelFromTypes(types: readonly string[]): PlaceLevel | null {
  if (types.includes('country')) return 'country';
  if (types.includes('administrative_area_level_1')) return 'region';
  if (types.includes('administrative_area_level_2')) return 'district';
  if (types.includes('locality') || types.includes('postal_town')) return 'town';
  if (types.some(type => type === 'neighborhood' || type.startsWith('sublocality'))) return 'neighbourhood';
  return null;
}
